import React, { useState, useEffect, useCallback } from 'react';
import { getIrrigationConfig, getIrrigationCredentials, saveIrrigationCredentials, getIrrigationSchedules, getIrrigationHistory, createIrrigationSchedule, deleteIrrigationSchedule, cancelIrrigationSchedule, resetStuckIrrigation, stopAllIrrigation } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Droplets, Plus, Loader2, Trash2, Clock, MapPin, Calendar, Key, CheckCircle2, XCircle, AlertTriangle, StopCircle, OctagonX, Timer, RotateCw } from 'lucide-react';

// Countdown Timer Component for running irrigation
const IrrigationTimer = ({ schedule }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [progress, setProgress] = useState(0);
    
    useEffect(() => {
        if (schedule.status !== 'RUNNING' || !schedule.circuit_started_at) {
            setTimeLeft(null);
            setProgress(0);
            return;
        }
        
        const duration = schedule.duration_minutes * 60; // in seconds
        
        const updateTimer = () => {
            const startTime = new Date(schedule.circuit_started_at).getTime();
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000);
            const remaining = Math.max(0, duration - elapsed);
            
            setTimeLeft(remaining);
            setProgress(Math.min(100, (elapsed / duration) * 100));
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        
        return () => clearInterval(interval);
    }, [schedule.circuit_started_at, schedule.duration_minutes, schedule.status]);
    
    if (schedule.status !== 'RUNNING') return null;
    
    const formatTime = (seconds) => {
        if (seconds === null) return '--:--';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m}:${s.toString().padStart(2, '0')}`;
    };
    
    const currentCircuit = schedule.current_circuit_index || 1;
    const totalCircuits = schedule.total_circuits || schedule.circuits?.length || 1;
    const currentRepeat = schedule.current_repeat || 1;
    const totalRepeats = schedule.total_repeats || schedule.repeat_count || 1;
    
    return (
        <div className="mt-3 p-3 bg-blue-100 rounded-lg border border-blue-200">
            {/* Repeat and Circuit Counter */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                    {totalRepeats > 1 && (
                        <div className="flex items-center gap-1 text-sm font-medium text-blue-700">
                            <RotateCw className="h-4 w-4" />
                            <span>Durchgang {currentRepeat}/{totalRepeats}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1 text-sm font-medium text-blue-700">
                        <Droplets className="h-4 w-4" />
                        <span>Kreislauf {currentCircuit}/{totalCircuits}</span>
                    </div>
                </div>
            </div>
            
            {/* Current circuit name */}
            {schedule.current_circuit_name && (
                <p className="text-xs text-blue-600 mb-2">
                    Aktiv: {schedule.current_circuit_name}
                </p>
            )}
            
            {/* Timer */}
            <div className="flex items-center gap-2 mb-2">
                <Timer className="h-4 w-4 text-blue-600" />
                <span className="text-lg font-bold text-blue-800 font-mono">
                    {formatTime(timeLeft)}
                </span>
                <span className="text-sm text-blue-600">verbleibend</span>
            </div>
            
            {/* Progress bar */}
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-blue-500 mt-1 text-right">{Math.round(progress)}%</p>
        </div>
    );
};

export const IrrigationControl = () => {
    const [config, setConfig] = useState(null);
    const [credentials, setCredentials] = useState({ portal_email: '', portal_password: '', has_credentials: false });
    const [schedules, setSchedules] = useState([]);
    const [history, setHistory] = useState([]);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stopping, setStopping] = useState(false);
    
    // Dialogs
    const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [immediateDialogOpen, setImmediateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    
    // Credential form
    const [credForm, setCredForm] = useState({ portal_email: '', portal_password: '' });
    
    // Schedule form
    const [scheduleForm, setScheduleForm] = useState({
        location: '',
        circuits: [],
        scheduled_date: '',
        scheduled_time: '06:00',
        duration_minutes: 10,
        pause_minutes: 5,
        repeat_count: 1
    });
    
    // Immediate start form (same as schedule but without date/time)
    const [immediateForm, setImmediateForm] = useState({
        location: '',
        circuits: [],
        duration_minutes: 10,
        pause_minutes: 5,
        repeat_count: 1
    });

    // Calculate total duration and end time
    const calculateTimeInfo = (form, startTime = null) => {
        const circuitCount = form.circuits?.length || 0;
        if (circuitCount === 0) return null;
        
        const durationPerCircuit = form.duration_minutes || 10;
        const pauseBetween = form.pause_minutes || 5;
        const repeats = form.repeat_count || 1;
        
        // Total time = (circuits * duration + (circuits-1) * pause) * repeats + (repeats-1) * pause
        const timePerRepeat = (circuitCount * durationPerCircuit) + ((circuitCount - 1) * pauseBetween);
        const totalMinutes = (timePerRepeat * repeats) + ((repeats - 1) * pauseBetween);
        
        const start = startTime ? new Date(`${form.scheduled_date}T${form.scheduled_time}`) : new Date();
        const end = new Date(start.getTime() + totalMinutes * 60 * 1000);
        
        return {
            totalMinutes,
            startTime: start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            endTime: end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            startDate: start.toLocaleDateString('de-DE'),
            endDate: end.toLocaleDateString('de-DE')
        };
    };

    useEffect(() => {
        loadData();
    }, []);

    // Auto-refresh every 10 seconds when there's a running irrigation
    useEffect(() => {
        const hasRunning = schedules.some(s => s.status === 'RUNNING');
        if (!hasRunning) return;
        
        const interval = setInterval(() => {
            // Silent refresh - only update schedules
            getIrrigationSchedules().then(res => {
                setSchedules(res.data);
            }).catch(() => {});
        }, 10000);
        
        return () => clearInterval(interval);
    }, [schedules]);

    const loadData = async () => {
        try {
            const [configRes, credsRes, schedulesRes, historyRes] = await Promise.all([
                getIrrigationConfig(),
                getIrrigationCredentials(),
                getIrrigationSchedules(),
                getIrrigationHistory()
            ]);
            setConfig(configRes.data);
            setCredentials(credsRes.data);
            setSchedules(schedulesRes.data);
            setHistory(historyRes.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Bewässerungsdaten');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCredentials = async () => {
        if (!credForm.portal_email || !credForm.portal_password) {
            toast.error('Bitte alle Felder ausfüllen');
            return;
        }
        setSaving(true);
        try {
            await saveIrrigationCredentials(credForm);
            toast.success('Zugangsdaten gespeichert');
            setCredentialsDialogOpen(false);
            loadData();
        } catch (err) {
            // Silent error
            const errorMsg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
            toast.error(`Fehler beim Speichern: ${errorMsg}`);
        } finally {
            setSaving(false);
        }
    };

    const openCredentialsDialog = () => {
        setCredForm({ portal_email: credentials.portal_email || '', portal_password: '' });
        setCredentialsDialogOpen(true);
    };

    const openScheduleDialog = () => {
        if (!credentials.has_credentials) {
            toast.error('Bitte zuerst Zugangsdaten hinterlegen');
            return;
        }
        // Set default date to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setScheduleForm({
            location: 'sinsheim',
            circuits: [],
            scheduled_date: tomorrow.toISOString().split('T')[0],
            scheduled_time: '06:00',
            duration_minutes: 10,
            pause_minutes: 5,
            repeat_count: 1
        });
        setScheduleDialogOpen(true);
    };

    const openImmediateDialog = () => {
        if (!credentials.has_credentials) {
            toast.error('Bitte zuerst Zugangsdaten hinterlegen');
            return;
        }
        setImmediateForm({
            location: 'sinsheim',
            circuits: [],
            duration_minutes: 10,
            pause_minutes: 5,
            repeat_count: 1
        });
        setImmediateDialogOpen(true);
    };

    const toggleCircuit = (circuit) => {
        setScheduleForm(prev => ({
            ...prev,
            circuits: prev.circuits.includes(circuit)
                ? prev.circuits.filter(c => c !== circuit)
                : [...prev.circuits, circuit]
        }));
    };

    const toggleImmediateCircuit = (circuit) => {
        setImmediateForm(prev => ({
            ...prev,
            circuits: prev.circuits.includes(circuit)
                ? prev.circuits.filter(c => c !== circuit)
                : [...prev.circuits, circuit]
        }));
    };

    const selectAllCircuits = () => {
        if (!config || !scheduleForm.location) return;
        const allCircuits = config[scheduleForm.location]?.circuits || [];
        setScheduleForm(prev => ({
            ...prev,
            circuits: prev.circuits.length === allCircuits.length ? [] : [...allCircuits]
        }));
    };

    const selectAllImmediateCircuits = () => {
        if (!config || !immediateForm.location) return;
        const allCircuits = config[immediateForm.location]?.circuits || [];
        setImmediateForm(prev => ({
            ...prev,
            circuits: prev.circuits.length === allCircuits.length ? [] : [...allCircuits]
        }));
    };

    const handleCreateSchedule = async () => {
        if (!scheduleForm.location || scheduleForm.circuits.length === 0) {
            toast.error('Bitte Standort und mindestens einen Kreislauf auswählen');
            return;
        }
        if (!scheduleForm.scheduled_date || !scheduleForm.scheduled_time) {
            toast.error('Bitte Datum und Uhrzeit auswählen');
            return;
        }
        setSaving(true);
        try {
            await createIrrigationSchedule(scheduleForm);
            toast.success('Bewässerung geplant');
            setScheduleDialogOpen(false);
            loadData();
        } catch (err) {
            // Silent error
            const errorMsg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleStartImmediate = async () => {
        if (!immediateForm.location || immediateForm.circuits.length === 0) {
            toast.error('Bitte Standort und mindestens einen Kreislauf auswählen');
            return;
        }
        setSaving(true);
        try {
            // Send with start_immediately flag - backend will handle timing
            const scheduleData = {
                ...immediateForm,
                scheduled_date: new Date().toISOString().split('T')[0],
                scheduled_time: '00:00', // Will be overridden by backend
                start_immediately: true
            };
            await createIrrigationSchedule(scheduleData);
            toast.success('Bewässerung wird gestartet...');
            setImmediateDialogOpen(false);
            loadData();
        } catch (err) {
            // Silent error
            const errorMsg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (schedule) => {
        setSelectedSchedule(schedule);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        try {
            await deleteIrrigationSchedule(selectedSchedule.id);
            toast.success('Zeitplan gelöscht');
            setDeleteDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleCancelRunning = async (schedule) => {
        if (!confirm(`Bewässerung "${schedule.location_name}" wirklich abbrechen?`)) return;
        try {
            await resetStuckIrrigation(schedule.id);
            toast.success('Bewässerung als fehlgeschlagen markiert');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Abbrechen');
        }
    };

    const handleStopAllIrrigation = async () => {
        if (!confirm('ALLE Bewässerungen im Hide and Grow Portal stoppen? Dies stoppt auch tatsächlich laufende Bewässerungen!')) return;
        setStopping(true);
        try {
            const res = await stopAllIrrigation();
            toast.success(res.data.message);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Stoppen');
        } finally {
            setStopping(false);
        }
    };

    const formatDateTime = (dateStr, timeStr) => {
        const date = new Date(`${dateStr}T${timeStr}`);
        return date.toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Geplant</span>;
            case 'RUNNING':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">Läuft...</span>;
            case 'COMPLETED':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Abgeschlossen</span>;
            case 'COMPLETED_WITH_ERRORS':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Mit Fehlern</span>;
            case 'FAILED':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Fehlgeschlagen</span>;
            case 'CANCELLED':
                return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">Abgebrochen</span>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    const selectedLocationCircuits = config && scheduleForm.location 
        ? config[scheduleForm.location]?.circuits || []
        : [];

    const selectedImmediateCircuits = config && immediateForm.location 
        ? config[immediateForm.location]?.circuits || []
        : [];

    return (
        <div className="space-y-6" data-testid="irrigation-control">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <Droplets className="h-5 w-5 text-blue-500" />
                        Bewässerungssteuerung
                    </h2>
                    <p className="text-sm text-muted-foreground">Hide and Grow Portal Automation</p>
                </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
                <Button variant="outline" onClick={openCredentialsDialog} className="w-full justify-start">
                    <Key className="h-4 w-4 mr-2" />
                    Zugangsdaten
                </Button>
                <Button onClick={openScheduleDialog} disabled={!credentials.has_credentials} className="w-full justify-start">
                    <Plus className="h-4 w-4 mr-2" />
                    Bewässerung planen
                </Button>
                <Button 
                    variant="secondary" 
                    onClick={openImmediateDialog} 
                    disabled={!credentials.has_credentials} 
                    className="w-full justify-start bg-blue-100 hover:bg-blue-200 text-blue-700"
                >
                    <Droplets className="h-4 w-4 mr-2" />
                    Sofort bewässern
                </Button>
            </div>

            {/* Credentials Status */}
            {!credentials.has_credentials && (
                <Card className="border-amber-300 bg-amber-50">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <div>
                            <p className="font-medium text-amber-800">Zugangsdaten erforderlich</p>
                            <p className="text-sm text-amber-600">
                                Bitte hinterlegen Sie die Zugangsdaten für das Hide and Grow Portal.
                            </p>
                        </div>
                        <Button variant="outline" size="sm" className="ml-auto" onClick={openCredentialsDialog}>
                            Einrichten
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Pending Schedules */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Geplante Bewässerungen
                    </CardTitle>
                    {schedules.some(s => s.status === 'RUNNING') && (
                        <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={handleStopAllIrrigation}
                            disabled={stopping}
                            className="flex items-center gap-1"
                        >
                            {stopping ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <OctagonX className="h-4 w-4" />
                            )}
                            Alles stoppen
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {schedules.length === 0 ? (
                        <p className="text-center py-6 text-muted-foreground">
                            Keine Bewässerungen geplant
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {schedules.map(schedule => (
                                <div 
                                    key={schedule.id}
                                    className={`p-4 border rounded-lg ${schedule.status === 'RUNNING' ? 'border-blue-300 bg-blue-50' : ''}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-semibold">{schedule.location_name}</span>
                                                {getStatusBadge(schedule.status)}
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                <Calendar className="h-3 w-3 inline mr-1" />
                                                {formatDateTime(schedule.scheduled_date, schedule.scheduled_time)}
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {schedule.circuits.length} Kreisläufe × {schedule.duration_minutes} Min
                                                {schedule.repeat_count > 1 && ` × ${schedule.repeat_count} Durchgänge`}
                                                {' '}= ~{(() => {
                                                    const singleCycle = schedule.circuits.length * schedule.duration_minutes + (schedule.circuits.length - 1) * (schedule.pause_minutes || 0);
                                                    const total = singleCycle * (schedule.repeat_count || 1) + ((schedule.repeat_count || 1) - 1) * (schedule.pause_minutes || 0);
                                                    const hours = Math.floor(total / 60);
                                                    const mins = total % 60;
                                                    return hours > 0 ? `${hours}h ${mins}min` : `${mins} Min`;
                                                })()}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {schedule.circuits.slice(0, 3).join(', ')}
                                                {schedule.circuits.length > 3 && ` +${schedule.circuits.length - 3} weitere`}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            {schedule.status === 'PENDING' && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => confirmDelete(schedule)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                            {schedule.status === 'RUNNING' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleCancelRunning(schedule)}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <StopCircle className="h-4 w-4 mr-1" />
                                                    Abbrechen
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Countdown Timer for running irrigation */}
                                    <IrrigationTimer schedule={schedule} />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* History */}
            {history.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center justify-between">
                            <span>Verlauf</span>
                            {history.length > 3 && (
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => setShowAllHistory(!showAllHistory)}
                                >
                                    {showAllHistory ? 'Weniger' : `Alle anzeigen (${history.length})`}
                                </Button>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {(showAllHistory ? history : history.slice(0, 3)).map(item => (
                                <div key={item.id} className="p-3 border rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{item.location_name}</span>
                                                {getStatusBadge(item.status)}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDateTime(item.scheduled_date, item.scheduled_time)}
                                                {item.completed_circuits && ` • ${item.completed_circuits.length} Kreisläufe`}
                                            </p>
                                        </div>
                                        {item.status === 'COMPLETED' && (
                                            <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                                        )}
                                        {item.status === 'FAILED' && (
                                            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                        )}
                                        {item.status === 'CANCELLED' && (
                                            <XCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                                        )}
                                    </div>
                                    {item.error && (
                                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 break-words">
                                            <strong>Fehler:</strong> {item.error}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Credentials Dialog */}
            <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Portal-Zugangsdaten</DialogTitle>
                        <DialogDescription>
                            Zugangsdaten für portal.hg.it4rent.org
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>E-Mail</Label>
                            <Input
                                type="email"
                                value={credForm.portal_email}
                                onChange={(e) => setCredForm({ ...credForm, portal_email: e.target.value })}
                                placeholder="email@beispiel.de"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Passwort</Label>
                            <Input
                                type="password"
                                value={credForm.portal_password}
                                onChange={(e) => setCredForm({ ...credForm, portal_password: e.target.value })}
                                placeholder={credentials.has_credentials ? '••••••••' : 'Passwort eingeben'}
                            />
                            {credentials.has_credentials && (
                                <p className="text-xs text-muted-foreground">
                                    Leer lassen um bestehendes Passwort zu behalten
                                </p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCredentialsDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveCredentials} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Schedule Dialog */}
            <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Bewässerung planen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 overflow-y-auto flex-1">
                        {/* Location */}
                        <div className="space-y-2">
                            <Label>Standort</Label>
                            <Select 
                                value={scheduleForm.location} 
                                onValueChange={(v) => setScheduleForm({ ...scheduleForm, location: v, circuits: [] })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Standort wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {config && Object.entries(config).map(([key, loc]) => (
                                        <SelectItem key={key} value={key}>
                                            {loc.name} ({loc.circuits.length} Kreisläufe)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Circuits */}
                        {scheduleForm.location && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Kreisläufe ({scheduleForm.circuits.length} ausgewählt)</Label>
                                    <Button variant="ghost" size="sm" onClick={selectAllCircuits}>
                                        {scheduleForm.circuits.length === selectedLocationCircuits.length ? 'Keine' : 'Alle'}
                                    </Button>
                                </div>
                                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                                    {selectedLocationCircuits.map(circuit => (
                                        <div key={circuit} className="flex items-center gap-2">
                                            <Checkbox
                                                id={circuit}
                                                checked={scheduleForm.circuits.includes(circuit)}
                                                onCheckedChange={() => toggleCircuit(circuit)}
                                            />
                                            <label htmlFor={circuit} className="text-sm cursor-pointer flex-1">
                                                {circuit}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Datum</Label>
                                <Input
                                    type="date"
                                    value={scheduleForm.scheduled_date}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Uhrzeit</Label>
                                <Input
                                    type="time"
                                    value={scheduleForm.scheduled_time}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Duration */}
                        <div className="space-y-2">
                            <Label>Dauer pro Kreislauf (Minuten)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    max="120"
                                    value={scheduleForm.duration_minutes}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, duration_minutes: parseInt(e.target.value) || 10 })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">Min (max. 120)</span>
                            </div>
                            <div className="flex gap-2">
                                {[5, 10, 15, 20, 30].map(d => (
                                    <Button
                                        key={d}
                                        variant={scheduleForm.duration_minutes === d ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setScheduleForm({ ...scheduleForm, duration_minutes: d })}
                                    >
                                        {d} Min
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Pause between circuits */}
                        <div className="space-y-2">
                            <Label>Pause zwischen Kreisläufen (Minuten)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="3"
                                    max="15"
                                    value={scheduleForm.pause_minutes}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, pause_minutes: Math.min(15, Math.max(3, parseInt(e.target.value) || 5)) })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">Min (3-15)</span>
                            </div>
                            <div className="flex gap-2">
                                {[3, 5, 7, 10, 15].map(p => (
                                    <Button
                                        key={p}
                                        variant={scheduleForm.pause_minutes === p ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setScheduleForm({ ...scheduleForm, pause_minutes: p })}
                                    >
                                        {p} Min
                                    </Button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Wartezeit nach jedem Kreislauf bevor der nächste startet
                            </p>
                        </div>

                        {/* Repeat count */}
                        <div className="space-y-2">
                            <Label>Wiederholungen</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={scheduleForm.repeat_count}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, repeat_count: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">× (1-10)</span>
                            </div>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map(r => (
                                    <Button
                                        key={r}
                                        variant={scheduleForm.repeat_count === r ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setScheduleForm({ ...scheduleForm, repeat_count: r })}
                                    >
                                        {r}×
                                    </Button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Gesamter Durchgang wird X-mal wiederholt (mit Pause dazwischen)
                            </p>
                        </div>

                        {/* Summary with Start/End Time */}
                        {scheduleForm.circuits.length > 0 && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm font-medium text-blue-800">
                                    Zusammenfassung
                                </p>
                                <p className="text-sm text-blue-700">
                                    {scheduleForm.circuits.length} Kreisläufe × {scheduleForm.duration_minutes} Min 
                                    {scheduleForm.repeat_count > 1 && ` × ${scheduleForm.repeat_count} Durchgänge`}
                                    {scheduleForm.circuits.length > 1 && ` + ${(scheduleForm.circuits.length - 1) * scheduleForm.repeat_count} Pausen`}
                                    {scheduleForm.repeat_count > 1 && ` + ${scheduleForm.repeat_count - 1} Durchgangspausen`}
                                </p>
                                {(() => {
                                    const timeInfo = calculateTimeInfo(scheduleForm, true);
                                    if (!timeInfo) return null;
                                    const hours = Math.floor(timeInfo.totalMinutes / 60);
                                    const mins = timeInfo.totalMinutes % 60;
                                    return (
                                        <>
                                            <p className="text-sm text-blue-700 font-semibold">
                                                = {hours > 0 ? `${hours}h ${mins}min` : `${mins} Minuten`} Gesamtdauer
                                            </p>
                                            <div className="mt-2 pt-2 border-t border-blue-200 flex items-center gap-4">
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-4 w-4 text-blue-600" />
                                                    <span className="text-sm text-blue-800 font-medium">
                                                        Start: {timeInfo.startTime} Uhr
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-blue-400">→</span>
                                                    <span className="text-sm text-blue-800 font-medium">
                                                        Ende: ~{timeInfo.endTime} Uhr
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCreateSchedule} disabled={saving || scheduleForm.circuits.length === 0}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Bewässerung planen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Zeitplan löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie diese geplante Bewässerung wirklich löschen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Immediate Start Dialog */}
            <Dialog open={immediateDialogOpen} onOpenChange={setImmediateDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Droplets className="h-5 w-5 text-blue-500" />
                            Sofort bewässern
                        </DialogTitle>
                        <DialogDescription>
                            Bewässerung startet sofort nach Bestätigung
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4 overflow-y-auto flex-1">
                        {/* Location */}
                        <div className="space-y-2">
                            <Label>Standort</Label>
                            <Select 
                                value={immediateForm.location} 
                                onValueChange={(v) => setImmediateForm({ ...immediateForm, location: v, circuits: [] })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Standort wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {config && Object.entries(config).map(([key, loc]) => (
                                        <SelectItem key={key} value={key}>
                                            {loc.name} ({loc.circuits.length} Kreisläufe)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Circuits */}
                        {immediateForm.location && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Kreisläufe ({immediateForm.circuits.length} ausgewählt)</Label>
                                    <Button variant="ghost" size="sm" onClick={selectAllImmediateCircuits}>
                                        {immediateForm.circuits.length === selectedImmediateCircuits.length ? 'Keine' : 'Alle'}
                                    </Button>
                                </div>
                                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                                    {selectedImmediateCircuits.map(circuit => (
                                        <div key={circuit} className="flex items-center gap-2">
                                            <Checkbox
                                                id={`imm-${circuit}`}
                                                checked={immediateForm.circuits.includes(circuit)}
                                                onCheckedChange={() => toggleImmediateCircuit(circuit)}
                                            />
                                            <label htmlFor={`imm-${circuit}`} className="text-sm cursor-pointer flex-1">
                                                {circuit}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Duration */}
                        <div className="space-y-2">
                            <Label>Dauer pro Kreislauf (Minuten)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    max="120"
                                    value={immediateForm.duration_minutes}
                                    onChange={(e) => setImmediateForm({ ...immediateForm, duration_minutes: parseInt(e.target.value) || 10 })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">Min (max. 120)</span>
                            </div>
                            <div className="flex gap-2">
                                {[5, 10, 15, 20, 30].map(d => (
                                    <Button
                                        key={d}
                                        variant={immediateForm.duration_minutes === d ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setImmediateForm({ ...immediateForm, duration_minutes: d })}
                                    >
                                        {d} Min
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Pause between circuits */}
                        <div className="space-y-2">
                            <Label>Pause zwischen Kreisläufen (Minuten)</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="3"
                                    max="15"
                                    value={immediateForm.pause_minutes}
                                    onChange={(e) => setImmediateForm({ ...immediateForm, pause_minutes: Math.min(15, Math.max(3, parseInt(e.target.value) || 5)) })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">Min (3-15)</span>
                            </div>
                            <div className="flex gap-2">
                                {[3, 5, 7, 10, 15].map(p => (
                                    <Button
                                        key={p}
                                        variant={immediateForm.pause_minutes === p ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setImmediateForm({ ...immediateForm, pause_minutes: p })}
                                    >
                                        {p} Min
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Repeat count */}
                        <div className="space-y-2">
                            <Label>Wiederholungen</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={immediateForm.repeat_count}
                                    onChange={(e) => setImmediateForm({ ...immediateForm, repeat_count: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) })}
                                    onFocus={(e) => e.target.select()}
                                    className="w-24"
                                />
                                <span className="text-sm text-muted-foreground">× (1-10)</span>
                            </div>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map(r => (
                                    <Button
                                        key={r}
                                        variant={immediateForm.repeat_count === r ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setImmediateForm({ ...immediateForm, repeat_count: r })}
                                    >
                                        {r}×
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Summary with Start/End Time */}
                        {immediateForm.circuits.length > 0 && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm font-medium text-blue-800">
                                    Zusammenfassung
                                </p>
                                <p className="text-sm text-blue-700">
                                    {immediateForm.circuits.length} Kreisläufe × {immediateForm.duration_minutes} Min 
                                    {immediateForm.repeat_count > 1 && ` × ${immediateForm.repeat_count} Durchgänge`}
                                    {immediateForm.circuits.length > 1 && ` + ${(immediateForm.circuits.length - 1) * immediateForm.repeat_count} Pausen`}
                                    {immediateForm.repeat_count > 1 && ` + ${immediateForm.repeat_count - 1} Durchgangspausen`}
                                </p>
                                {(() => {
                                    const timeInfo = calculateTimeInfo(immediateForm, false);
                                    if (!timeInfo) return null;
                                    const hours = Math.floor(timeInfo.totalMinutes / 60);
                                    const mins = timeInfo.totalMinutes % 60;
                                    return (
                                        <>
                                            <p className="text-sm text-blue-700 font-semibold">
                                                = {hours > 0 ? `${hours}h ${mins}min` : `${mins} Minuten`} Gesamtdauer
                                            </p>
                                            <div className="mt-2 pt-2 border-t border-blue-200 flex items-center gap-4">
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-4 w-4 text-blue-600" />
                                                    <span className="text-sm text-blue-800 font-medium">
                                                        Start: {timeInfo.startTime} Uhr
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <span className="text-blue-400">→</span>
                                                    <span className="text-sm text-blue-800 font-medium">
                                                        Ende: ~{timeInfo.endTime} Uhr
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImmediateDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button 
                            onClick={handleStartImmediate} 
                            disabled={saving || immediateForm.circuits.length === 0}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            <Droplets className="h-4 w-4 mr-2" />
                            Jetzt starten
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default IrrigationControl;
